import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { RankingPlayer, UserProfile } from '@types';
import {
  generateAvatarFromAddress,
  generateNameFromAddress,
} from '@/shared/utils';
import { useLeaderboardWithPeriod } from '@hooks';
import type {
  LeaderboardMode,
  LeaderboardPeriod,
  LeaderboardRewardsConfig,
} from '@services';
import { Loading, Tooltip } from '@/components/common';
import bgTop1 from '@/assets/ranking/bg-top-1.webp';
import bgTop2 from '@/assets/ranking/bg-top-2.webp';
import bgTop3 from '@/assets/ranking/bg-top-3.webp';
import pointIcon from '@/assets/header/point.webp';
import rankCupIcon from '@/assets/ranking/rank-cup.webp';
import goldIcon from '@/assets/header/gold.webp';
import energyIcon from '@/assets/header/energy.webp';
import timerIcon from '@/assets/ranking/timer.webp';
import giftIcon from '@/assets/ranking/gift.webp';

// ============ Custom SVG Icons ============
const WeeklyIcon = ({
  size = 16,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3"
      y="6"
      width="18"
      height="15"
      rx="2"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path d="M3 10H21" stroke={color} strokeWidth="2" />
    <path d="M8 3V7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M16 3V7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <rect x="7" y="13" width="3" height="3" rx="0.5" fill={color} />
  </svg>
);

const MonthlyIcon = ({
  size = 16,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3"
      y="6"
      width="18"
      height="15"
      rx="2"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path d="M3 10H21" stroke={color} strokeWidth="2" />
    <path d="M8 3V7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M16 3V7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <rect x="6" y="12.5" width="2.5" height="2.5" rx="0.5" fill={color} />
    <rect x="10.75" y="12.5" width="2.5" height="2.5" rx="0.5" fill={color} />
    <rect x="15.5" y="12.5" width="2.5" height="2.5" rx="0.5" fill={color} />
    <rect x="6" y="16" width="2.5" height="2.5" rx="0.5" fill={color} />
    <rect x="10.75" y="16" width="2.5" height="2.5" rx="0.5" fill={color} />
  </svg>
);

const TrophyIcon = ({
  size = 16,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 15C15.3137 15 18 12.3137 18 9V4H6V9C6 12.3137 8.68629 15 12 15Z"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M6 6H4C3.44772 6 3 6.44772 3 7V8C3 9.65685 4.34315 11 6 11"
      stroke={color}
      strokeWidth="2"
    />
    <path
      d="M18 6H20C20.5523 6 21 6.44772 21 7V8C21 9.65685 19.6569 11 18 11"
      stroke={color}
      strokeWidth="2"
    />
    <path d="M12 15V18" stroke={color} strokeWidth="2" />
    <path d="M8 21H16" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M9 18H15" stroke={color} strokeWidth="2" />
  </svg>
);

const ClockIcon = ({
  size = 12,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" fill="none" />
    <path
      d="M12 7V12L15 15"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Gift/Treasure icon for rewards button
const GiftIcon = ({
  size = 18,
  color = 'currentColor',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="3"
      y="12"
      width="18"
      height="9"
      rx="1"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <rect
      x="5"
      y="8"
      width="14"
      height="4"
      rx="1"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path d="M12 8V21" stroke={color} strokeWidth="2" />
    <path
      d="M12 8C12 8 12 5 9 4C6 3 6 6 6 6C6 6 6 8 9 8H12"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M12 8C12 8 12 5 15 4C18 3 18 6 18 6C18 6 18 8 15 8H12"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
  </svg>
);

// ============ Period Countdown Hook ============
const useCountdown = (targetDate: string | null) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const difference = new Date(targetDate).getTime() - Date.now();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

// ============ Mode Toggle Switch ============
interface ModeToggleProps {
  activeMode: LeaderboardMode;
  onModeChange: (mode: LeaderboardMode) => void;
}

const ModeToggle = ({ activeMode, onModeChange }: ModeToggleProps) => {
  const isPVE = activeMode === 'pve';

  return (
    <motion.button
      onClick={() => onModeChange(isPVE ? 'pvp' : 'pve')}
      className="relative flex items-center rounded-full overflow-hidden"
      style={{
        width: 115,
        height: 43,
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(20, 20, 30, 0.8) 100%)',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        boxShadow: 'inset 0 3px 6px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
      }}
      whileTap={{ scale: 0.97 }}
    >
      {/* Background glow based on mode */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: isPVE
            ? 'linear-gradient(90deg, rgba(34, 197, 94, 0.3) 0%, transparent 70%)'
            : 'linear-gradient(90deg, transparent 30%, rgba(239, 68, 68, 0.3) 100%)',
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Labels - PVE on left, PVP on right */}
      <div className="absolute inset-0 flex items-center justify-between px-3 z-10">
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: 13,
            color: isPVE ? 'transparent' : 'rgba(255,255,255,0.4)',
            transition: 'color 0.3s',
          }}
        >
          PVE
        </span>
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: 13,
            color: !isPVE ? 'transparent' : 'rgba(255,255,255,0.4)',
            transition: 'color 0.3s',
          }}
        >
          PVP
        </span>
      </div>

      {/* Sliding knob with label */}
      <motion.div
        className="absolute rounded-full flex items-center justify-center overflow-hidden"
        style={{
          width: 52,
          height: 34,
          top: 3,
          background: isPVE
            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
          border: isPVE ? '2px solid #4ade80' : '2px solid #f87171',
          boxShadow: isPVE
            ? '0 0 12px rgba(34, 197, 94, 0.6), inset 0 1px 0 rgba(255,255,255,0.3)'
            : '0 0 12px rgba(239, 68, 68, 0.6), inset 0 1px 0 rgba(255,255,255,0.3)',
        }}
        animate={{ left: isPVE ? 3 : 55 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      >
        {/* Shimmer effect on knob */}
        <motion.div
          className="absolute inset-0 opacity-50"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
          }}
          animate={{ x: ['-100%', '200%'] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
        />
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: 14,
            color: '#fff',
            textShadow: '0 2px 4px rgba(0,0,0,0.4)',
            position: 'relative',
            zIndex: 10,
          }}
        >
          {isPVE ? 'PVE' : 'PVP'}
        </span>
      </motion.div>
    </motion.button>
  );
};

// ============ Period Tabs Component ============
interface PeriodTabsProps {
  activePeriod: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
}

const PeriodTabs = ({ activePeriod, onPeriodChange }: PeriodTabsProps) => {
  const periods: {
    id: LeaderboardPeriod;
    label: string;
    Icon: React.FC<{ size?: number; color?: string }>;
  }[] = [
    { id: 'weekly', label: 'Weekly', Icon: WeeklyIcon },
    { id: 'monthly', label: 'Monthly', Icon: MonthlyIcon },
    { id: 'all-time', label: 'All Time', Icon: TrophyIcon },
  ];

  const getActiveStyle = (id: LeaderboardPeriod) => {
    switch (id) {
      case 'all-time':
        return {
          background:
            'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
          boxShadow:
            '0 0 14px rgba(255, 215, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.4)',
          border: '2px solid #FFE066',
        };
      case 'monthly':
        return {
          background:
            'linear-gradient(135deg, #a855f7 0%, #8b5cf6 50%, #7c3aed 100%)',
          boxShadow:
            '0 0 14px rgba(168, 85, 247, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
          border: '2px solid #c084fc',
        };
      default:
        return {
          background:
            'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
          boxShadow:
            '0 0 14px rgba(59, 130, 246, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
          border: '2px solid #60a5fa',
        };
    }
  };

  return (
    <div className="flex items-center gap-1 w-fit">
      {periods.map((period) => {
        const isActive = activePeriod === period.id;
        return (
          <motion.button
            key={period.id}
            onClick={() => onPeriodChange(period.id)}
            className="relative px-3 py-2 rounded-xl font-medium transition-all"
            style={{
              fontFamily: 'Lilita One',
              fontSize: 12,
              color: isActive
                ? period.id === 'all-time'
                  ? '#1a1a2e'
                  : '#fff'
                : 'rgba(255, 255, 255, 0.5)',
              background: isActive ? undefined : 'rgba(0,0,0,0.3)',
              border: isActive ? undefined : '1px solid rgba(255,255,255,0.1)',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            {isActive && (
              <motion.div
                layoutId="activePeriodTab"
                className="absolute inset-0 rounded-xl"
                style={getActiveStyle(period.id)}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1.5">
              <period.Icon
                size={14}
                color={
                  isActive
                    ? period.id === 'all-time'
                      ? '#1a1a2e'
                      : '#fff'
                    : 'rgba(255, 255, 255, 0.5)'
                }
              />
              <span>{period.label}</span>
            </span>
          </motion.button>
        );
      })}
    </div>
  );
};

// ============ Timer Button with Countdown Tooltip ============
interface TimerButtonProps {
  periodEnd: string | null;
  period: LeaderboardPeriod;
}

const TimerButton = ({ periodEnd, period }: TimerButtonProps) => {
  const timeLeft = useCountdown(periodEnd);
  const formatNumber = (n: number) => n.toString().padStart(2, '0');

  const getTooltipContent = () => {
    if (period === 'all-time' || !periodEnd) {
      return (
        <div className="flex flex-col items-center gap-2 py-1">
          <div className="flex items-center gap-2">
            <TrophyIcon size={18} color="#FFD700" />
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: 14,
                color: '#FFD700',
              }}
            >
              Eternal Glory
            </span>
          </div>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
            No time limit - legends live forever!
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2 min-w-48">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 pb-1 border-b border-white/10">
          <ClockIcon size={14} color="#60a5fa" />
          <span
            style={{ fontFamily: 'Lilita One', fontSize: 14, color: '#60a5fa' }}
          >
            Season Countdown
          </span>
        </div>

        {/* Countdown Display */}
        <div className="flex items-center justify-center gap-2">
          {timeLeft.days > 0 && (
            <div className="flex flex-col items-center">
              <span
                style={{
                  fontFamily: 'Lilita One',
                  fontSize: 20,
                  color: '#a855f7',
                }}
              >
                {timeLeft.days}
              </span>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
                DAYS
              </span>
            </div>
          )}
          {timeLeft.days > 0 && (
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16 }}>
              :
            </span>
          )}
          <div className="flex flex-col items-center">
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: 20,
                color: '#3b82f6',
              }}
            >
              {formatNumber(timeLeft.hours)}
            </span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
              HRS
            </span>
          </div>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16 }}>
            :
          </span>
          <div className="flex flex-col items-center">
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: 20,
                color: '#3b82f6',
              }}
            >
              {formatNumber(timeLeft.minutes)}
            </span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
              MIN
            </span>
          </div>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16 }}>
            :
          </span>
          <div className="flex flex-col items-center">
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: 20,
                color: '#fbbf24',
              }}
            >
              {formatNumber(timeLeft.seconds)}
            </span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
              SEC
            </span>
          </div>
        </div>

        {/* Fun Footer */}
        <div
          className="text-center pt-1 border-t border-white/10"
          style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}
        >
          Hurry! Climb before time runs out!
        </div>
      </div>
    );
  };

  return (
    <Tooltip content={getTooltipContent()} side="bottom">
      <motion.button
        className="relative flex items-center justify-center rounded-xl overflow-hidden"
        style={{
          width: 44,
          height: 44,
          background:
            period === 'all-time'
              ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(109, 40, 217, 0.15) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)',
          border:
            period === 'all-time'
              ? '1px solid rgba(139, 92, 246, 0.3)'
              : '2px solid rgba(59, 130, 246, 0.4)',
          boxShadow:
            period === 'all-time'
              ? 'inset 0 1px 0 rgba(255,255,255,0.05)'
              : '0 0 12px rgba(59, 130, 246, 0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <img
          src={timerIcon}
          alt="Timer"
          className="w-7 h-7"
          style={{
            filter:
              period === 'all-time'
                ? 'grayscale(0.5) opacity(0.6)'
                : 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
          }}
        />
      </motion.button>
    </Tooltip>
  );
};

// ============ Rich Tooltip Content for Rewards ============
interface RewardTierRowProps {
  rank: string;
  gold: number;
  energy: number;
  color: string;
  bgColor: string;
}

const RewardTierRow = ({
  rank,
  gold,
  energy,
  color,
  bgColor,
}: RewardTierRowProps) => (
  <div
    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg"
    style={{ background: bgColor }}
  >
    <span
      style={{
        fontFamily: 'Lilita One',
        fontSize: 13,
        color: color,
        minWidth: 50,
      }}
    >
      {rank}
    </span>
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1">
        <img src={goldIcon} alt="Gold" className="w-4 h-4" />
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: 13,
            color: '#fbbf24',
          }}
        >
          +{gold.toLocaleString()}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <img src={energyIcon} alt="Energy" className="w-4 h-4" />
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: 13,
            color: '#22d3d1',
          }}
        >
          +{energy}
        </span>
      </div>
    </div>
  </div>
);

// ============ Rewards Info Button with Rich Tooltip ============
interface RewardsButtonProps {
  rewards: LeaderboardRewardsConfig | null;
  period: LeaderboardPeriod;
}

const RewardsButton = ({ rewards, period }: RewardsButtonProps) => {
  const hasRewards = rewards && period !== 'all-time';
  const periodLabel =
    period === 'weekly'
      ? 'Weekly'
      : period === 'monthly'
        ? 'Monthly'
        : 'All-Time';

  const getTooltipContent = () => {
    if (period === 'all-time' || !rewards) {
      return (
        <div className="flex flex-col items-center gap-2 py-1">
          <div className="flex items-center gap-2">
            <TrophyIcon size={20} color="#FFD700" />
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: 16,
                color: '#FFD700',
              }}
            >
              Hall of Fame
            </span>
            <TrophyIcon size={20} color="#FFD700" />
          </div>
          <span
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
              textAlign: 'center',
            }}
          >
            Legends live forever here!
          </span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
            No resets. Eternal glory awaits!
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2 min-w-60">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 pb-1 border-b border-white/10">
          <GiftIcon size={18} color="#FFD700" />
          <span
            style={{ fontFamily: 'Lilita One', fontSize: 15, color: '#FFD700' }}
          >
            {periodLabel} Rewards
          </span>
          <GiftIcon size={18} color="#FFD700" />
        </div>

        {/* Reward Tiers */}
        <div className="flex flex-col gap-1.5">
          {/* Champion - Rank 1 */}
          <RewardTierRow
            rank="#1"
            gold={rewards.top1.gold}
            energy={rewards.top1.maxEnergyBoost}
            color="#FFD700"
            bgColor="rgba(255, 215, 0, 0.15)"
          />

          {/* Elite - Rank 2-3 */}
          <RewardTierRow
            rank="#2 - #3"
            gold={rewards.top2to3.gold}
            energy={rewards.top2to3.maxEnergyBoost}
            color="#C0C0C0"
            bgColor="rgba(192, 192, 192, 0.1)"
          />

          {/* Contender - Rank 4-10 */}
          <RewardTierRow
            rank="#4 - #10"
            gold={rewards.top4to10.gold}
            energy={rewards.top4to10.maxEnergyBoost}
            color="#CD7F32"
            bgColor="rgba(205, 127, 50, 0.1)"
          />
        </div>

        {/* Duration Info */}
        <div
          className="flex items-center justify-center gap-1.5 pt-1 border-t border-white/10"
          style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}
        >
          <ClockIcon size={12} color="rgba(255,255,255,0.6)" />
          <span>Energy boost lasts {rewards.top1.durationDays} days!</span>
        </div>

        {/* Fun Footer */}
        <div
          className="text-center"
          style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}
        >
          Climb the ranks and claim your treasure!
        </div>
      </div>
    );
  };

  return (
    <Tooltip content={getTooltipContent()} side="bottom">
      <motion.button
        className="relative flex items-center justify-center rounded-xl overflow-hidden"
        style={{
          width: 44,
          height: 44,
          background: hasRewards
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 165, 0, 0.2) 100%)'
            : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(109, 40, 217, 0.15) 100%)',
          border: hasRewards
            ? '2px solid rgba(255, 215, 0, 0.5)'
            : '1px solid rgba(139, 92, 246, 0.3)',
          boxShadow: hasRewards
            ? '0 0 16px rgba(255, 215, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
            : 'inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <img
          src={giftIcon}
          alt="Rewards"
          className="w-7 h-7"
          style={{
            filter: hasRewards
              ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              : 'grayscale(0.5) opacity(0.6)',
          }}
        />
      </motion.button>
    </Tooltip>
  );
};

// ============ Header Bar ============
interface HeaderBarProps {
  activeMode: LeaderboardMode;
  onModeChange: (mode: LeaderboardMode) => void;
  activePeriod: LeaderboardPeriod;
  onPeriodChange: (period: LeaderboardPeriod) => void;
  rewards: LeaderboardRewardsConfig | null;
  periodEnd: string | null;
}

const HeaderBar = ({
  activeMode,
  onModeChange,
  activePeriod,
  onPeriodChange,
  rewards,
  periodEnd,
}: HeaderBarProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-2 w-full"
    >
      {/* Row 1: Timer | PeriodTabs | Rewards */}
      <div
        className="flex items-center justify-between gap-2 px-2 py-2 rounded-2xl w-full"
        style={{
          background:
            'linear-gradient(180deg, rgba(15, 8, 30, 0.95) 0%, rgba(30, 15, 50, 0.9) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.25)',
          boxShadow:
            '0 4px 20px rgba(88, 28, 135, 0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        {/* Timer Button - Left */}
        <TimerButton periodEnd={periodEnd} period={activePeriod} />

        {/* Period Tabs - Center */}
        <PeriodTabs
          activePeriod={activePeriod}
          onPeriodChange={onPeriodChange}
        />

        {/* Rewards Button - Right */}
        <RewardsButton rewards={rewards} period={activePeriod} />
      </div>

      {/* Row 2: ModeToggle - Centered */}
      <div className="flex items-center justify-center">
        <ModeToggle activeMode={activeMode} onModeChange={onModeChange} />
      </div>
    </motion.div>
  );
};

// ============ Get Reward for Rank ============
const getRewardForRank = (
  rank: number,
  rewards: LeaderboardRewardsConfig | null,
  period: LeaderboardPeriod
): { gold: number; energy: number } | null => {
  if (!rewards || period === 'all-time') return null;
  if (rank === 1)
    return { gold: rewards.top1.gold, energy: rewards.top1.maxEnergyBoost };
  if (rank >= 2 && rank <= 3)
    return {
      gold: rewards.top2to3.gold,
      energy: rewards.top2to3.maxEnergyBoost,
    };
  if (rank >= 4 && rank <= 10)
    return {
      gold: rewards.top4to10.gold,
      energy: rewards.top4to10.maxEnergyBoost,
    };
  return null;
};

// ============ Podium Component ============
interface PodiumProps {
  topThree: RankingPlayer[];
  mode: LeaderboardMode;
  rewards: LeaderboardRewardsConfig | null;
  period: LeaderboardPeriod;
}

const Podium = ({ topThree, mode, rewards, period }: PodiumProps) => {
  const getPodiumConfig = (rank: number) => {
    switch (rank) {
      case 1:
        return {
          height: 194,
          width: 150,
          bgImage: bgTop1,
          glowColor: 'rgba(255, 215, 0, 0.6)',
          borderGlow: '0 0 20px rgba(255, 215, 0, 0.5)',
          crownEmoji: '',
          avatarSize: 56,
          avatarBorder: '3px solid #FFD700',
          rankBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          textColor: '#5E0009',
        };
      case 2:
        return {
          height: 174,
          width: 125,
          bgImage: bgTop2,
          glowColor: 'rgba(192, 192, 192, 0.5)',
          borderGlow: '0 0 15px rgba(192, 192, 192, 0.4)',
          crownEmoji: '',
          avatarSize: 48,
          avatarBorder: '2px solid #C0C0C0',
          rankBg: 'linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 100%)',
          textColor: '#043447',
        };
      case 3:
        return {
          height: 168,
          width: 120,
          bgImage: bgTop3,
          glowColor: 'rgba(205, 127, 50, 0.5)',
          borderGlow: '0 0 15px rgba(205, 127, 50, 0.4)',
          crownEmoji: '',
          avatarSize: 44,
          avatarBorder: '2px solid #CD7F32',
          rankBg: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
          textColor: '#37345D',
        };
      default:
        return {
          height: 140,
          width: 100,
          bgImage: bgTop2,
          glowColor: 'rgba(100, 100, 100, 0.3)',
          borderGlow: 'none',
          crownEmoji: '',
          avatarSize: 40,
          avatarBorder: '2px solid #666',
          rankBg: '#666',
          textColor: '#333',
        };
    }
  };

  // Display order: 2nd, 1st, 3rd
  const displayOrder = [topThree[1], topThree[0], topThree[2]].filter(Boolean);

  return (
    <div className="flex items-end justify-center gap-1 w-full mt-3">
      {displayOrder.map((player, index) => {
        if (!player) return null;
        const config = getPodiumConfig(player.rank);
        const reward = getRewardForRank(player.rank, rewards, period);

        return (
          <motion.div
            key={player.id}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              delay: player.rank === 1 ? 0.2 : index * 0.15,
              type: 'spring',
              stiffness: 200,
              damping: 20,
            }}
            className="relative flex flex-col items-center"
            style={{
              width: config.width,
              height: config.height,
              filter: `drop-shadow(${config.borderGlow})`,
            }}
          >
            {/* Background Card */}
            <div
              className="absolute inset-0 rounded-xl overflow-hidden"
              style={{
                backgroundImage: `url(${config.bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center bottom',
              }}
            />

            {/* Content Overlay */}
            <div className="relative z-10 flex flex-col items-center pt-2 h-full">
              {/* Avatar Container */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
                style={{
                  width: config.avatarSize + 8,
                  height: config.avatarSize + 8,
                }}
              >
                {/* Glow Ring */}
                <div
                  className="absolute inset-0 rounded-full animate-pulse"
                  style={{
                    background: config.glowColor,
                    filter: 'blur(8px)',
                  }}
                />
                {/* Avatar */}
                <img
                  src={generateAvatarFromAddress(player.id, config.avatarSize)}
                  alt={generateNameFromAddress(player.id)}
                  className="relative rounded-full z-10"
                  style={{
                    width: config.avatarSize,
                    height: config.avatarSize,
                    border: config.avatarBorder,
                    margin: '4px',
                  }}
                />
                {/* Rank Badge */}
                <div
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center rounded-full"
                  style={{
                    width: 22,
                    height: 22,
                    background: config.rankBg,
                    border: '2px solid rgba(0,0,0,0.3)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Lilita One',
                      fontSize: 12,
                      color: '#fff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {player.rank}
                  </span>
                </div>
              </motion.div>

              {/* Player Info */}
              <div className="flex flex-col items-center mt-2 px-2">
                {/* Name */}
                <span
                  className="text-center line-clamp-1 font-medium"
                  style={{
                    fontSize: player.rank === 1 ? 13 : 11,
                    color: config.textColor,
                    maxWidth: config.width - 16,
                    textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                  }}
                >
                  {generateNameFromAddress(player.id)}
                </span>

                {/* Score */}
                <div
                  className="flex items-center gap-1 mt-0.5"
                  style={{ filter: 'drop-shadow(0 1px 1px #000)' }}
                >
                  <img
                    src={mode === 'pvp' ? rankCupIcon : pointIcon}
                    alt="Score"
                    className="w-4 h-4"
                  />
                  <span
                    style={{
                      fontFamily: 'Lilita One',
                      fontSize: player.rank === 1 ? 16 : 14,
                      color: '#FFE500',
                    }}
                  >
                    {player.score.toLocaleString()}
                  </span>
                </div>

                {/* Reward Display */}
                {reward && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full"
                    style={{
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <div className="flex items-center gap-0.5">
                      <img src={goldIcon} alt="Gold" className="w-3 h-3" />
                      <span
                        className="text-[9px] text-amber-400"
                        style={{ fontFamily: 'Lilita One' }}
                      >
                        {reward.gold}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <img src={energyIcon} alt="Energy" className="w-3 h-3" />
                      <span
                        className="text-[9px] text-cyan-400"
                        style={{ fontFamily: 'Lilita One' }}
                      >
                        +{reward.energy}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ============ Leaderboard List Item Component ============
interface LeaderboardListItemProps {
  player: RankingPlayer;
  mode: LeaderboardMode;
  index: number;
  rewards: LeaderboardRewardsConfig | null;
  period: LeaderboardPeriod;
}

const LeaderboardListItem = ({
  player,
  mode,
  index,
  rewards,
  period,
}: LeaderboardListItemProps) => {
  const reward = getRewardForRank(player.rank, rewards, period);

  const tierColors: Record<
    string,
    { bg: string; border: string; glow: string }
  > = {
    top4to10: {
      bg: 'rgba(205, 127, 50, 0.1)',
      border: 'rgba(205, 127, 50, 0.3)',
      glow: '0 0 8px rgba(205, 127, 50, 0.1)',
    },
  };

  const tierStyle = reward ? tierColors.top4to10 : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="relative flex flex-row items-center isolate backdrop-blur-sm w-full"
      style={{
        boxSizing: 'border-box',
        padding: '4px 12px 4px 4px',
        gap: '4px',
        height: '52px',
        background: tierStyle ? tierStyle.bg : 'rgba(139, 139, 139, 0.5)',
        border: `1px solid ${tierStyle ? tierStyle.border : '#000000'}`,
        borderRadius: '12px',
        boxShadow: tierStyle ? tierStyle.glow : 'none',
      }}
    >
      {/* Rank Box */}
      <div
        style={{
          width: '44px',
          height: '44px',
          background: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontFamily: 'Lilita One',
            fontWeight: 400,
            fontSize: '24px',
            lineHeight: '27px',
            textAlign: 'center',
            color: '#FFFFFF',
          }}
        >
          {player.rank}
        </span>
      </div>

      {/* Avatar */}
      <img
        src={generateAvatarFromAddress(player.id, 44)}
        alt={generateNameFromAddress(player.id)}
        className="rounded-lg border-2 border-[#1e3a8a70]"
        style={{ width: 40, height: 40 }}
      />

      {/* Name & Score */}
      <div
        className="flex flex-col justify-center flex-grow"
        style={{ gap: '2px' }}
      >
        <span
          className="truncate"
          style={{
            fontFamily: 'Lilita One',
            fontSize: '16px',
            lineHeight: '18px',
            color: '#FFFFFF',
          }}
        >
          {generateNameFromAddress(player.id)}
        </span>
        <div className="flex items-center gap-1">
          <img
            src={mode === 'pvp' ? rankCupIcon : pointIcon}
            alt="Point"
            style={{
              width: '16px',
              height: '16px',
              filter: 'drop-shadow(1px 1px 0px #000000)',
            }}
          />
          <span
            style={{
              fontFamily: 'Lilita One',
              fontSize: '14px',
              lineHeight: '16px',
              color: '#FFFFFF',
            }}
          >
            {player.score.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Inline Reward Display */}
      {reward && (
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
          style={{
            background:
              'linear-gradient(135deg, rgba(205, 127, 50, 0.2) 0%, rgba(139, 69, 19, 0.15) 100%)',
            border: '1px solid rgba(205, 127, 50, 0.3)',
          }}
        >
          <div className="flex items-center gap-0.5">
            <img src={goldIcon} alt="Gold" className="w-3 h-3" />
            <span
              className="text-[9px] text-amber-400"
              style={{ fontFamily: 'Lilita One' }}
            >
              {reward.gold}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <img src={energyIcon} alt="Energy" className="w-3 h-3" />
            <span
              className="text-[9px] text-cyan-400"
              style={{ fontFamily: 'Lilita One' }}
            >
              +{reward.energy}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ============ Leaderboard List Component ============
interface LeaderboardListProps {
  players: RankingPlayer[];
  mode: LeaderboardMode;
  rewards: LeaderboardRewardsConfig | null;
  period: LeaderboardPeriod;
}

const LeaderboardList = ({
  players,
  mode,
  rewards,
  period,
}: LeaderboardListProps) => {
  return (
    <div className="flex flex-col gap-2">
      {players.map((player, index) => (
        <LeaderboardListItem
          key={player.id}
          player={player}
          mode={mode}
          index={index}
          rewards={rewards}
          period={period}
        />
      ))}
    </div>
  );
};

// ============ My Rank Display Component ============
interface MyRankDisplayProps {
  player: RankingPlayer;
  mode: LeaderboardMode;
  rewards: LeaderboardRewardsConfig | null;
  period: LeaderboardPeriod;
}

const MyRankDisplay = ({
  player,
  mode,
  rewards,
  period,
}: MyRankDisplayProps) => {
  const myReward = getRewardForRank(player.rank, rewards, period);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="relative flex flex-row items-center isolate backdrop-blur-sm w-full overflow-hidden"
      style={{
        boxSizing: 'border-box',
        padding: '4px 12px 4px 4px',
        gap: '4px',
        height: myReward ? '62px' : '52px',
        background:
          mode === 'pve'
            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 49.04%, #15803d 100%)'
            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 49.04%, #b91c1c 100%)',
        border: '1px solid #000000',
        borderRadius: '12px',
      }}
    >
      {/* Shimmer Effect */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
          animation: 'shimmer 2s infinite',
        }}
      />

      {/* Rank Box */}
      <div
        style={{
          width: '44px',
          height: '44px',
          background: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontFamily: 'Lilita One',
            fontSize: '24px',
            lineHeight: '27px',
            color: '#FFFFFF',
          }}
        >
          {player.rank}
        </span>
      </div>

      {/* Avatar */}
      <img
        src={generateAvatarFromAddress(player.id, 44)}
        alt={generateNameFromAddress(player.id)}
        className="rounded-lg border-2 border-white/30"
        style={{ width: 40, height: 40 }}
      />

      {/* Name & Score */}
      <div
        className="flex flex-col justify-center flex-grow relative z-10"
        style={{ gap: '2px' }}
      >
        <span
          className="truncate"
          style={{
            fontFamily: 'Lilita One',
            fontSize: '16px',
            lineHeight: '18px',
            color: '#FFFFFF',
          }}
        >
          {generateNameFromAddress(player.id)} (Me)
        </span>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <img
              src={mode === 'pvp' ? rankCupIcon : pointIcon}
              alt="Point"
              style={{
                width: '16px',
                height: '16px',
                filter: 'drop-shadow(1px 1px 0px #000000)',
              }}
            />
            <span
              style={{
                fontFamily: 'Lilita One',
                fontSize: '14px',
                lineHeight: '16px',
                color: '#FFFFFF',
              }}
            >
              {player.score.toLocaleString()}
            </span>
          </div>
          {/* My Reward Preview */}
          {myReward && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg"
              style={{
                background:
                  'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.3) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
            >
              <div className="flex items-center gap-1">
                <img src={goldIcon} alt="Gold" className="w-3 h-3" />
                <span
                  className="text-[10px] text-amber-400"
                  style={{ fontFamily: 'Lilita One' }}
                >
                  +{myReward.gold}
                </span>
              </div>
              <div className="w-px h-3 bg-white/20" />
              <div className="flex items-center gap-1">
                <img src={energyIcon} alt="Energy" className="w-3 h-3" />
                <span
                  className="text-[10px] text-cyan-400"
                  style={{ fontFamily: 'Lilita One' }}
                >
                  +{myReward.energy}
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============ Main Component ============
interface RankingContentProps {
  userProfile: UserProfile;
}

export const RankingContent = ({ userProfile }: RankingContentProps) => {
  const [activeMode, setActiveMode] = useState<LeaderboardMode>('pve');
  const [activePeriod, setActivePeriod] = useState<LeaderboardPeriod>('weekly');

  const { players, isLoading, error, periodEnd, rewards } =
    useLeaderboardWithPeriod(activeMode, activePeriod);

  // Find current user in leaderboard
  const userInLeaderboard = players.find(
    (p) => p.id.toLowerCase() === userProfile.address?.toLowerCase()
  );

  const currentUser: RankingPlayer = userInLeaderboard
    ? {
        ...userInLeaderboard,
        username: userProfile.username || '',
      }
    : {
        id: userProfile.address,
        username: userProfile.username || '',
        rank: players.length + 1,
        score: activeMode === 'pve' ? userProfile.scores || 0 : 0,
        time: undefined,
      };

  const topThree = players.slice(0, 3);
  const restOfPlayers = players.slice(3);

  // Show loading state
  if (isLoading) {
    return <Loading text="Loading leaderboard" size="lg" fullScreen />;
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-red-400 text-lg">Failed to load leaderboard</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col">
      {/* Shimmer Animation Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Fixed Header */}
      <div className="fixed top-[130px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto flex flex-col items-center px-3 z-40 pb-2">
        {/* Header Bar */}
        <HeaderBar
          activeMode={activeMode}
          onModeChange={setActiveMode}
          activePeriod={activePeriod}
          onPeriodChange={setActivePeriod}
          rewards={rewards}
          periodEnd={periodEnd}
        />

        {/* Podium for Top 3 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeMode}-${activePeriod}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <Podium
              topThree={topThree}
              mode={activeMode}
              rewards={rewards}
              period={activePeriod}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Scrollable Leaderboard List */}
      <motion.div
        className="fixed top-[450px] bottom-0 pb-[170px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-6 overflow-y-auto z-30 scrollbar-thin scrollbar-thumb-purple-500 scrollbar-track-purple-900/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeMode}-${activePeriod}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <LeaderboardList
              players={restOfPlayers}
              mode={activeMode}
              rewards={rewards}
              period={activePeriod}
            />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Fixed My Rank Display at Bottom */}
      <div className="fixed bottom-[95px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-6 z-40">
        <MyRankDisplay
          player={currentUser}
          mode={activeMode}
          rewards={rewards}
          period={activePeriod}
        />
      </div>
    </div>
  );
};
